import React, { useState } from 'react';
import { UserAccount, AuthState } from './auth-types';
import { multiAccountVault } from './multi-account-vault';
import { ProfileFaceIdModal } from './ProfileFaceIdModal';

interface AuthModalProps {
  onClose: () => void;
  onOpenFaceIdEnroll?: (user: UserAccount) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose }) => {
  const [authState, setAuthState] = useState<AuthState>(multiAccountVault.getState());
  const [profileToEdit, setProfileToEdit] = useState<UserAccount | null>(null);

  const handleSwitchAccount = (userId: string) => {
    multiAccountVault.login(userId);
    setAuthState(multiAccountVault.getState());
  };

  const handleLogout = () => {
    multiAccountVault.logout();
    setAuthState(multiAccountVault.getState());
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="w-full max-w-lg rounded-2xl border border-border-subtle bg-bg-surface p-6 shadow-2xl space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border-subtle pb-3">
            <div className="flex items-center space-x-2">
              <span className="text-xl">🔐</span>
              <div>
                <h2 className="text-base font-bold text-text-primary">Identity & Multi-Account Vault</h2>
                <p className="text-xs text-text-muted">Switch roles, manage permissions, or configure Face ID</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-text-muted hover:text-text-primary text-sm font-mono"
            >
              ✕
            </button>
          </div>

          {/* Current Session Summary */}
          {authState.currentUser ? (
            <div className="rounded-xl border border-accent-primary/40 bg-accent-primary/5 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative h-12 w-12 rounded-full border border-accent-primary overflow-hidden bg-bg-primary">
                  <img src={authState.currentUser.avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
                  {authState.currentUser.faceIdProfile?.enrolled && (
                    <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-emerald-500 border-2 border-bg-surface" title="Face ID Enrolled" />
                  )}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-bold text-text-primary">{authState.currentUser.displayName}</span>
                    <span className="rounded-full bg-accent-primary/20 text-accent-primary px-2 py-0.5 text-[10px] font-mono uppercase font-bold">
                      {authState.currentUser.role}
                    </span>
                  </div>
                  <p className="text-[11px] font-mono text-text-muted">{authState.currentUser.email}</p>
                  <p className="text-[11px] font-mono text-emerald-400 font-semibold">
                    Balance: {(authState.currentUser.balanceMicroCredits / 1000000).toFixed(2)} Credits ({authState.currentUser.balanceMicroCredits.toLocaleString()} µC)
                  </p>
                </div>
              </div>

              <div className="flex flex-col space-y-1">
                <button
                  type="button"
                  onClick={() => setProfileToEdit(authState.currentUser)}
                  className="rounded-lg border border-border-subtle bg-bg-surface px-2.5 py-1 text-xs font-mono text-text-primary hover:border-accent-primary"
                >
                  Edit Face ID
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 text-xs font-mono text-rose-300 hover:bg-rose-500/20"
                >
                  Log Out
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-center">
              <p className="text-xs font-mono text-amber-300">No active session. Select an account below to log in.</p>
            </div>
          )}

          {/* Account Switcher List */}
          <div className="space-y-2">
            <h3 className="text-xs font-mono text-text-muted uppercase tracking-wider">Identities on this Device</h3>
            {authState.accounts.length === 0 ? (
              <div className="p-4 rounded-xl border border-border-subtle bg-bg-primary/50 text-center">
                <p className="text-xs font-mono text-text-muted">No previously logged in identities on this device.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {authState.accounts.map((account) => {
                  const isActive = authState.currentUser?.id === account.id;
                  return (
                    <div
                      key={account.id}
                      className={`flex items-center justify-between p-3 rounded-xl border transition ${
                        isActive
                          ? 'border-accent-primary bg-bg-surface shadow-md'
                          : 'border-border-subtle bg-bg-primary hover:border-border-default'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-9 w-9 rounded-full border border-border-subtle overflow-hidden bg-bg-surface">
                          <img src={account.avatarUrl} alt={account.displayName} className="h-full w-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-semibold text-text-primary">{account.displayName}</span>
                            <span className="rounded bg-bg-surface border border-border-subtle px-1.5 py-0.5 text-[9px] font-mono uppercase text-text-muted">
                              {account.role}
                            </span>
                          </div>
                          <p className="text-[10px] font-mono text-text-muted">{account.email}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {isActive ? (
                          <span className="rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 px-2 py-0.5 text-[10px] font-mono font-bold">
                            ACTIVE
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleSwitchAccount(account.id)}
                            className="rounded-lg bg-accent-primary text-bg-primary px-3 py-1 text-xs font-mono font-semibold hover:opacity-90 transition"
                          >
                            Switch
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            multiAccountVault.removeAccount(account.id);
                            setAuthState(multiAccountVault.getState());
                          }}
                          className="text-text-muted hover:text-rose-400 text-xs px-1"
                          title="Remove identity from this device"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end pt-2 border-t border-border-subtle">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-bg-primary border border-border-subtle px-4 py-2 text-xs font-mono text-text-primary hover:border-accent-primary"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {profileToEdit && (
        <ProfileFaceIdModal
          user={profileToEdit}
          onClose={() => {
            setProfileToEdit(null);
            setAuthState(multiAccountVault.getState());
          }}
        />
      )}
    </>
  );
};
